// 로그인 흐름 조립 — 돌아온 토큰 받기 · 보관 · 만료 전 재발급 · 로그인/로그아웃
import { sessionStore } from '../../../composition/plugin-runtime';
import { keep, loadKept, markIntent, readIntent } from '../adapters/session-keeper';
import { refreshToken } from '../adapters/token-refresh';
import { loginUrl, readCallback, refreshDelayMs, returnToOf, type IssuedToken } from '../domain/login';

export interface AuthParams {
  readonly platformUrl: string;
  readonly tenant: string;
}

let params: AuthParams | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
// 로그아웃·종료할 때마다 올린다 — 그 전에 떠난 재발급 요청의 응답을 버리는 기준
let epoch = 0;

function accept(issued: IssuedToken | null): void {
  clearTimeout(timer);
  keep(issued);
  sessionStore.set(issued);
  if (issued) timer = setTimeout(() => void renew(), refreshDelayMs(issued.expiresAt, Date.now()));
}

/** 플랫폼 세션이 살아 있으면 새 토큰을 받는다. 못 받아도 지금 토큰은 만료까지 그대로 쓴다 */
async function renew(): Promise<void> {
  const active = params;
  const started = epoch;
  if (!active) return;
  try {
    const issued = await refreshToken(active.platformUrl, active.tenant);
    // 기다리는 사이 로그아웃했거나 플러그인이 내려갔으면 버린다 — 로그아웃 직후 도착한 응답이 다시 로그인시키지 않게
    if (issued && epoch === started) accept(issued);
  } catch (error) {
    // 재발급 실패는 로그아웃이 아니다 — 지금 토큰은 만료까지 쓰고, 만료되면 로그인 버튼이 다시 보인다
    if (import.meta.env?.DEV) console.warn('[auth] 토큰 재발급 실패', error);
  }
}

/** 부팅 — 동기다. 브라우저가 아니면(테스트·서버 렌더) 아무것도 하지 않는다 */
export function startAuth(next: AuthParams): void {
  params = next;
  if (typeof window === 'undefined') return;
  const intent = readIntent();
  const callback = readCallback(window.location.hash, Date.now());
  if (callback) {
    // 토큰을 주소창·방문 기록에서 지운다 — 새로 고침·주소 복사에 실려 나가지 않게
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + callback.rest);
    // 이 탭에서 「로그인」을 눌러 다녀온 경우에만 받는다. 누가 보낸 링크의 조각에 든 토큰(남의 계정)은 받지 않는다 —
    // 받으면 그 사람 계정으로 로그인된 채 결제하게 된다
    if (intent === 'login') {
      markIntent(null);
      accept(callback.issued);
      return;
    }
  }
  if (intent === 'login') markIntent(null); // 로그인 화면에서 토큰 없이 돌아옴(취소) — 표식을 걷는다
  if (intent === 'out') return; // 이 탭에서 로그아웃했다 — 「로그인」을 누르기 전에는 다시 들어오지 않는다
  const kept = loadKept(Date.now());
  if (kept) accept(kept);
  else void renew(); // 다른 탭·지난 방문에서 로그인했으면 버튼을 누르지 않아도 로그인된다
}

export function stopAuth(): void {
  clearTimeout(timer);
  epoch += 1;
  params = null;
  sessionStore.set(null);
}

/** 플랫폼 로그인 화면으로 보낸다. 끝나면 지금 보던 주소로 돌아온다 */
export function login(): void {
  if (!params) return;
  markIntent('login');
  window.location.assign(loginUrl(params.platformUrl, params.tenant, returnToOf(window.location.href)));
}

/**
 * 이 MVP 에서 로그아웃한다 — 이 탭의 토큰을 지우고, 새로 고쳐도 다시 들어오지 않게 표식을 남긴다.
 * 플랫폼 세션(다른 MVP 와 함께 쓰는 로그인)은 그대로라, 「로그인」을 누르면 제공자 화면 없이 바로 들어온다.
 * 다른 탭·새 탭은 플랫폼 세션으로 로그인된다 — 기기에서 완전히 나가려면 플랫폼 로그아웃이 필요하다
 */
export function logout(): void {
  epoch += 1;
  markIntent('out');
  accept(null);
}
